// @flow
import React, { useEffect } from 'react';
import type { Element } from 'react';

import { connect } from 'react-redux';
import compose from 'lodash/fp/compose';
import { compact, isEmpty } from 'lodash';

import { changeTitle } from '@redux/components/Metadata/actions';

import { useBreakpoint } from '@hooks';
import { FINDER_TREATMENT } from '@routes';
import { Router, withTranslation } from '@i18n';
import { LoadingSpinner } from '@components';

import SearchResultListItem from './SearchResultListItem';

import {
  StyledWrapper,
  StyledList,
  StyledSpinnerContainer,
  StyledMobileFiltersPreview
} from './styles';

import type { Props, TitleTuple } from './types';

const SearchResultList = ({
  objectList,
  city,
  position,
  department,
  loading,
  t,
  changeTitle
}: Props): Element<any> => {
  const isMobile: boolean = useBreakpoint('mobile', 'desktop');

  const currentPosition: ?string = t('TreatmentLists', { returnObjects: true })?.[position];
  const currentDepartment: ?string = t('departments', { returnObjects: true })?.[department];
  const appliedFilters: Array<?string> = [currentPosition, currentDepartment, city];
  const hasActiveFilters: boolean = !isEmpty(compact(appliedFilters));

  const clearFilters = () => {
    Router.pushRoute(FINDER_TREATMENT, { pageNumber: 1 });
  };

  const titleStringSuffix: string = [
    ['inCity', 'city', city],
    ['asPosition', 'position', currentPosition],
    ['inDepartment', 'department', currentDepartment]
  ].reduce(
    (suffix: string, [translationKey, attributeName, attributeValue]: TitleTuple): string => {
      if (isEmpty(attributeValue)) {
        return suffix;
      }

      return suffix + `  ${t(translationKey, { [attributeName]: attributeValue })}`;
    },
    ''
  );

  useEffect((): void => {
    changeTitle({
      IN: {
        en: t('Treatments', '') + titleStringSuffix
      }
    });
  }, [t('Treatments', ''), titleStringSuffix]);

  return (
    <StyledWrapper>
      {isMobile && hasActiveFilters && (
        <StyledMobileFiltersPreview values={appliedFilters} onClear={clearFilters} />
      )}

      {loading ? (
        <StyledSpinnerContainer>
          <LoadingSpinner />
        </StyledSpinnerContainer>
      ) : (
        <StyledList>
          {objectList.map(object => (
            <SearchResultListItem key={`result-list-item-${object.id}`} result={object} />
          ))}
        </StyledList>
      )}
    </StyledWrapper>
  );
};

SearchResultList.getInitialProps = (): {} => ({ namespacesRequired: ['Treatments'] });

const mapStateToProps = (): {} => ({});

export default compose(
  withTranslation('Treatments'),
  connect(
    mapStateToProps,
    { changeTitle }
  )
)(SearchResultList);
